import { Link, useSearchParams } from "react-router";
import { HomeTitle } from "./Hl";
import { t, type MessageKey } from "../i18n";
import { useSession } from "../session";
import { queryMessage } from "../lib";
import { LoginButtons } from "./LoginButtons";
import { LANDING_SOURCES, SOURCE_GROUPS } from "../guides";
import { GuideButton, GuideHost } from "./Guide";
import { SourceIcon } from "./brand-icons";
import { HealthStatus } from "./HealthStatus";
import { ExampleAnswer } from "./ExampleAnswer";
import { PlanStrip } from "./PlanStrip";
import { PriceNote } from "./PriceNote";
import { useBillingState } from "../billing";
import { ClientsBanner } from "./ClientsBanner";
import { CLIENTS_WORD } from "../lib";

const STEPS = ["home.step1", "home.step2", "home.step3"] as const;
const PRIVACY = ["readonly", "keys", "nothing", "revoke"] as const;

/** The landing page: exactly the composition of Home.dc.html (ADR-0031) - not an instruction.
 *  Signed in, the logo gives way to the Health status card and the sign-in buttons to «See your numbers». */
export function HomePage() {
  const { me } = useSession();
  const pending = me === undefined;
  const [search] = useSearchParams();
  const msg = queryMessage(search);
  const { state: billing } = useBillingState(Boolean(me));
  const plan = billing?.plan.state;

  return (
    <>
      {/* BL-132: one plan strip under the header, only when the plan needs the user (ADR-0045). */}
      {me && <PlanStrip state={billing} />}
      {/* BL-194: «New: clients» for everyone, above the hero (ADR-0062); an urgent plan strip stays first. */}
      <ClientsBanner />
      <section className={me ? "hero-row signed-in" : "hero-row"}>
        <div className="hero-copy">
          <p className="kicker">{t("home.kicker")}</p>
          <h1><HomeTitle text={t("home.title")} /></h1>
          <p className="hero-lead">{t("home.subtitle")}</p>
          {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}
          {me ? (
            <div className="hero-signed">
              <p className="hero-as">
                {t("home.signedAs")} <span>{me.email ?? me.name ?? ""}</span>
              </p>
              <div className="hero-actions">
                <Link className="btn btn-primary hero-cta" to="/preview">
                  {t("home.seeNumbers")}
                </Link>
                <Link className="text-link hero-link" to="/connections">
                  {t("home.addSource")} →
                </Link>
              </div>
            </div>
          ) : (
            // Unknown session: the guest's controls, hidden but taking their place (ADR-0050).
            <>
              <div className={`hero-actions${pending ? " pending" : ""}`} inert={pending} aria-hidden={pending || undefined}>
                <LoginButtons variant="hero" />
              </div>
              <p className={`hero-hint${pending ? " pending" : ""}`} aria-hidden={pending || undefined}>
                {t("home.hint")}
              </p>
            </>
          )}
          {/* The price is for those still choosing; a signed-in user has the plan strip and Account → Plan (BL-140). */}
          {!me && <PriceNote />}
          {/* No install link in the hero: the header's «Install in Claude or ChatGPT» is the way (owner 2026-11-02). */}
        </div>
        {me ? (
          <HealthStatus timeZone={me.timezone} blocked={plan === "expired" ? "subscribe" : plan === "paused" ? "card" : null} />
        ) : (
          <ExampleAnswer />
        )}
      </section>

      <section className="home-section home-sources">
        <div className="home-section-head">
          <h2>{t("home.sourcesTitle")}</h2>
          <p className="home-section-lead">{t("home.sourcesLead")}</p>
          <p className="home-section-lead home-clients-line">
            {t("home.sourcesClients", { count: CLIENTS_WORD })}{" "}
            <Link className="text-link" to="/for-agencies">
              {t("home.sourcesClientsLink")}&nbsp;→
            </Link>
          </p>
        </div>
        <div className="source-groups">
          {SOURCE_GROUPS.map((g) => (
            <div key={g} className="source-group">
              <div className="group-label">{t(`home.group.${g}` as MessageKey)}</div>
              <ul className="sources">
                {LANDING_SOURCES.filter((s) => s.group === g).map((s) => (
                  <li key={s.id}>
                    <GuideButton id={s.id} className="btn btn-secondary source-chip">
                      <SourceIcon id={s.id} size={14} />
                      {s.label}
                    </GuideButton>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="home-section">
        <h2>{t("home.stepsTitle")}</h2>
        <ol className="steps">
          {STEPS.map((k, i) => (
            <li key={k} className="card elev-sm">
              <span className="num-dot">{i + 1}</span>
              <div className="card-title">{t(`${k}.title`)}</div>
              <p className="card-body">{t(`${k}.text`)}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="privacy-band" id="privacy">
        <div className="privacy-head">
          <p className="kicker">{t("home.privacy.kicker")}</p>
          <h2>{t("home.privacy.title")}</h2>
          <p>{t("home.privacy.lead")}</p>
        </div>
        <div className="privacy-cols">
          {PRIVACY.map((k) => (
            <div key={k} className="privacy-col">
              <span className="privacy-rule" />
              <div className="privacy-col-title">{t(`home.privacy.${k}.title` as MessageKey)}</div>
              <p>{t(`home.privacy.${k}.text` as MessageKey)}</p>
            </div>
          ))}
        </div>
        <div className="privacy-links">
          <a href="/privacy.html">{t("home.privacy.policy")}</a>
          <a href="/terms.html">{t("footer.terms")}</a>
          <a href="/datadeletion">{t("footer.deletion")}</a>
        </div>
      </section>

      <GuideHost signedIn={Boolean(me)} />
    </>
  );
}
